import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  HeadphonesIcon, Activity, Shield, Cloud, Database, 
  FileCheck, Lightbulb, ArrowRight, CheckCircle2, Clock,
  Users, Zap, Phone, Globe, Cable
} from 'lucide-react';

export default function ServicesPage() {
  const services = [
    {
      id: 'help-desk',
      icon: HeadphonesIcon,
      title: 'Technical Support & Help Desk',
      subtitle: 'Fast, Friendly IT Support When You Need It',
      description: 'Your employees deserve quick answers to their IT problems. Our help desk team provides remote and on-site support, so your team can stay productive instead of waiting on hold.',
      features: [
        'Remote support via phone, chat, and screen share',
        'On-site technician visits when needed',
        'Ticketing system for tracking all issues',
        'Multiple SLA options (15 min, 1 hour, 4 hour)',
        'After-hours emergency support available',
        'End-user training and documentation',
      ],
      scenarios: [
        'Employee can\'t access email or files',
        'Software crashes or won\'t install',
        'New employee needs computer setup',
        'Printer or peripheral not working',
      ],
      highlight: 'col-span-1',
    },
    {
      id: 'network',
      icon: Activity,
      title: 'Network Monitoring & Management',
      subtitle: '24/7 Infrastructure Health Checks',
      description: 'We monitor your network infrastructure around the clock, catching problems before they cause downtime. Our proactive approach means fewer emergencies and smoother operations.',
      features: [
        '24/7/365 network monitoring',
        'Real-time performance dashboards',
        'Automated alerts for issues',
        'Router, switch, and firewall management',
        'Bandwidth optimization',
        'Regular health reports',
      ],
      scenarios: [
        'Internet slowdowns affecting productivity',
        'Network outages costing you money',
        'Unclear what\'s causing connection issues',
        'Need visibility into network usage',
      ],
      highlight: 'col-span-1 lg:col-span-2',
    },
    {
      id: 'security',
      icon: Shield,
      title: 'Cybersecurity & Protection',
      subtitle: 'Enterprise-Grade Security for SMBs',
      description: 'Cyber threats don\'t discriminate by company size. We provide the same security tools and practices used by large enterprises, scaled and priced for your business.',
      features: [
        'Managed firewall and intrusion prevention',
        'Endpoint protection (antivirus, EDR)',
        'Email security and phishing defense',
        'Security awareness training for staff',
        'Managed Detection & Response (MDR)',
        'Vulnerability scanning and patching',
      ],
      scenarios: [
        'Worried about ransomware attacks',
        'Employees clicking phishing emails',
        'Need to meet client security requirements',
        'Unsure if current protection is adequate',
      ],
      highlight: 'col-span-1 lg:col-span-2',
    },
    {
      id: 'cloud',
      icon: Cloud,
      title: 'Cloud Services & Management',
      subtitle: 'Microsoft 365, Azure & Cloud Hosting Experts',
      description: 'Whether you\'re migrating to the cloud or already there, we help you get the most out of your cloud investments. From Microsoft 365 administration to Azure infrastructure.',
      features: [
        'Microsoft 365 setup and management',
        'Azure cloud infrastructure',
        'Cloud migration planning and execution',
        'Hybrid cloud configurations',
        'License optimization and cost management',
        'Cloud security and compliance',
      ],
      scenarios: [
        'Moving from on-premise servers to cloud',
        'Microsoft 365 licensing confusion',
        'Need SharePoint or Teams setup',
        'Azure costs growing unexpectedly',
      ],
      highlight: 'col-span-1',
    },
    {
      id: 'backup',
      icon: Database,
      title: 'Backup & Disaster Recovery',
      subtitle: 'Business Continuity Planning',
      description: 'Data loss can cripple a business. Our backup solutions ensure your critical files, systems, and applications can be recovered quickly — whether from accidental deletion or a major disaster.',
      features: [
        'Automated daily backups',
        'Cloud and local backup options',
        'Rapid file and system recovery',
        'Disaster recovery planning',
        'Business continuity testing',
        'Compliance-ready retention policies',
      ],
      scenarios: [
        'Never tested if backups actually work',
        'No plan for server failure or fire',
        'Employee accidentally deleted critical files',
        'Need to meet data retention requirements',
      ],
      highlight: 'col-span-1',
    },
    {
      id: 'compliance',
      icon: FileCheck,
      title: 'Compliance Support',
      subtitle: 'HIPAA, PCI DSS & Regulatory Assistance',
      description: 'Regulatory compliance is complex, but non-compliance is costly. We help you implement the technical controls and documentation needed to meet industry requirements.',
      features: [
        'HIPAA compliance for healthcare',
        'PCI DSS for payment processing',
        'SOC 2 readiness assistance',
        'Data retention policy implementation',
        'Security documentation',
        'Audit preparation support',
      ],
      scenarios: [
        'Healthcare practice needing HIPAA compliance',
        'Accepting credit cards and need PCI DSS',
        'Enterprise clients requiring security audits',
        'Cyber insurance requirements',
      ],
      highlight: 'col-span-1',
    },
    {
      id: 'consulting',
      icon: Lightbulb,
      title: 'IT Consulting & Strategy',
      subtitle: 'Technology Planning for Growth',
      description: 'Technology should drive your business forward, not hold it back. Our consulting services help you plan IT investments, improve processes, and align technology with business goals.',
      features: [
        'Technology roadmap planning',
        'IT budget optimization',
        'Vendor evaluation and management',
        'Process improvement consulting',
        'Digital transformation guidance',
        'Quarterly business reviews',
      ],
      scenarios: [
        'Planning office expansion or relocation',
        'Evaluating new software or systems',
        'IT spending feels out of control',
        'Need strategic IT guidance',
      ],
      highlight: 'col-span-1',
    },
    {
      id: 'website',
      icon: Globe,
      title: 'Professional Website Design',
      subtitle: 'Starting at $550 — Fast Turnaround',
      description: 'Your website is your digital storefront. We design and build professional, mobile-responsive business websites that look great and convert visitors into customers — all at an affordable flat rate.',
      features: [
        'Custom professional design',
        'Mobile-responsive layout',
        'SEO-friendly structure',
        'Contact forms and lead capture',
        'Fast loading and optimized performance',
        'Content management training',
      ],
      scenarios: [
        'No website or outdated site hurting credibility',
        'Need a professional online presence fast',
        'Current site not mobile-friendly',
        'Want a website that actually generates leads',
      ],
      highlight: 'col-span-1',
    },
    {
      id: 'cabling',
      icon: Cable,
      title: 'Structured Cabling',
      subtitle: 'Professional Network Infrastructure',
      description: 'A reliable network starts with quality cabling. Our certified technicians design and install structured cabling systems that form the backbone of your business communications — from Cat6 runs to complete fiber optic deployments.',
      features: [
        'Cat5e / Cat6 / Cat6a copper cabling',
        'Single-mode & multi-mode fiber optic installation',
        'Patch panel termination & testing',
        'Server room & data closet buildouts',
        'Network rack installation & cable management',
        'Cable pathway & conduit routing',
        'Cable certification & testing reports',
        'MDF/IDF design & implementation',
      ],
      scenarios: [
        'New office buildout or tenant improvement',
        'Relocating or expanding your business',
        'Upgrading from outdated Cat5 cabling',
        'Need reliable, high-speed network infrastructure',
        'Server room cleanup or rack organization',
        'Adding new workstation or access point drops',
      ],
      highlight: 'col-span-1',
    },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-slate-950 py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-300">Full-Service Managed IT</span>
            </div>
            
            <h1 
              className="text-4xl sm:text-5xl font-black text-white mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Managed IT Services That{' '}
              <span className="text-gradient">Actually Work</span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8">
              From help desk support to strategic IT consulting, we provide everything 
              your business needs to run smoothly and securely.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/30"
                  data-testid="services-get-quote-btn"
                >
                  Get Custom Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-slate-500 text-white hover:bg-white/10"
                  data-testid="services-view-pricing-btn"
                >
                  View Pricing Plans
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                id={service.id}
                className="scroll-mt-24"
                data-testid={`service-${service.id}`}
              >
                <Card className={`border border-slate-200 hover:border-blue-500/30 transition-colors overflow-hidden ${index % 2 === 1 ? 'bg-slate-50' : 'bg-white'}`}>
                  <CardContent className="p-0">
                    <div className="grid lg:grid-cols-2 gap-0">
                      {/* Service Info */}
                      <div className="p-8 lg:p-12">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                          <service.icon className="w-7 h-7 text-blue-600" />
                        </div>
                        
                        <h2 
                          className="text-2xl sm:text-3xl font-black text-slate-900 mb-2"
                          style={{ fontFamily: 'Chivo, sans-serif' }}
                        >
                          {service.title}
                        </h2>
                        
                        <p className="text-blue-600 font-semibold mb-4">
                          {service.subtitle}
                        </p>
                        
                        <p className="text-slate-600 mb-8 leading-relaxed">
                          {service.description}
                        </p>
                        
                        <h3 className="font-bold text-slate-900 mb-4">What's Included:</h3>
                        <div className="grid sm:grid-cols-2 gap-3 mb-8">
                          {service.features.map((feature) => (
                            <div key={feature} className="flex items-start gap-2">
                              <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-slate-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                        
                        <Link to="/contact">
                          <Button 
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                            data-testid={`service-${service.id}-cta`}
                          >
                            Learn More
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                      
                      {/* Scenarios */}
                      <div className="bg-slate-900 p-8 lg:p-12 text-white">
                        <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                          <Users className="w-5 h-5 text-cyan-400" />
                          Common Scenarios We Solve
                        </h3>
                        
                        <div className="space-y-4">
                          {service.scenarios.map((scenario, idx) => (
                            <div 
                              key={scenario} 
                              className="flex items-start gap-3 p-4 bg-slate-800 rounded-lg"
                            >
                              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-blue-400 font-bold text-sm">{idx + 1}</span>
                              </div>
                              <p className="text-slate-300">{scenario}</p>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-8 p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-5 h-5 text-cyan-400" />
                            <span className="font-semibold">Quick Response Guarantee</span>
                          </div>
                          <p className="text-sm text-slate-300">
                            Most issues resolved within 15 minutes. On-site visits available same-day.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-black text-white mb-6"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Not Sure What You Need?
          </h2>
          <p className="text-lg text-slate-400 mb-8">
            Get a free IT assessment and we'll recommend the right services for your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-500/30"
                data-testid="services-bottom-cta"
              >
                Schedule Free Assessment
              </Button>
            </Link>
            <a href="tel:6026491314">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-500 text-white hover:bg-white/10"
              >
                <Phone className="w-4 h-4 mr-2" />
                (602) 649-1314
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
